import { ValidationError } from '@lender/utils';

import { getConditionalObject } from 'utils/data';

import { DeclarationType } from '../Declarations.constants';
import { customSetInForm } from '../Declarations.utils';

export const checkAreFieldsRequired = ({
  isServedInMilitary,
  isCurrentlyServing,
  isCurrentlyRetired,
  isNonActivatedMemberOfReserveNationalGuard,
  isSurvivingSpouse,
  entitlementCodeFromCoe,
  branchOfService,
  militaryStatus,
}) =>
  isServedInMilitary &&
  !(
    isCurrentlyServing ||
    isCurrentlyRetired ||
    isNonActivatedMemberOfReserveNationalGuard ||
    isSurvivingSpouse ||
    entitlementCodeFromCoe?.value ||
    branchOfService?.value ||
    militaryStatus?.value
  );

export const normalizeForm = ({ form, entityId, initialFormValues, prevIsServedInMilitary }) => {
  const {
    isServedInMilitary,
    isCurrentlyServing,
    currentlyServingDate,
    isCurrentlyRetired,
    isNonActivatedMemberOfReserveNationalGuard,
    isSurvivingSpouse,
    entitlementCodeFromCoe,
    branchOfService,
    militaryStatus,
    basicEntitlement,
    bonusEntitlement,
    totalEntitlementAmount,
    basicEntitlementManualUpdate,
    bonusEntitlementManualUpdate,
    firstTimeVaUse,
    isExemptVAFundingFee,
  } = form[entityId]?.declarations?.militaryService ?? {};

  const initialData = initialFormValues[entityId]?.declarations?.militaryService ?? {};

  const shouldSetFalsyValue = prevIsServedInMilitary && !isServedInMilitary;
  const shouldResetValue = isServedInMilitary && !prevIsServedInMilitary;

  const getFieldValue = (value, isCheckbox = true) => {
    if (shouldSetFalsyValue) {
      return isCheckbox ? false : '';
    }

    if (shouldResetValue) {
      return undefined;
    }

    return value;
  };

  return {
    ...form,
    [entityId]: {
      ...form[entityId],
      declarations: {
        ...form[entityId]?.declarations,
        militaryService: {
          isServedInMilitary,
          isCurrentlyServing: getFieldValue(isCurrentlyServing),
          currentlyServingDate:
            (isServedInMilitary &&
              isCurrentlyServing &&
              (currentlyServingDate || initialData.currentlyServingDate)) ||
            null,
          isCurrentlyRetired: getFieldValue(isCurrentlyRetired),
          isNonActivatedMemberOfReserveNationalGuard: getFieldValue(
            isNonActivatedMemberOfReserveNationalGuard,
          ),
          isSurvivingSpouse: getFieldValue(isSurvivingSpouse),
          entitlementCodeFromCoe: getFieldValue(entitlementCodeFromCoe, false),
          branchOfService: getFieldValue(branchOfService, false),
          militaryStatus: getFieldValue(militaryStatus, false),
          basicEntitlement: getFieldValue(basicEntitlement, false),
          bonusEntitlement: getFieldValue(bonusEntitlement, false),
          totalEntitlementAmount: getFieldValue(totalEntitlementAmount, false),
          basicEntitlementManualUpdate: getFieldValue(basicEntitlementManualUpdate),
          bonusEntitlementManualUpdate: getFieldValue(bonusEntitlementManualUpdate),
          firstTimeVaUse: getFieldValue(firstTimeVaUse, false),
          isExemptVAFundingFee: getFieldValue(isExemptVAFundingFee, false),
        },
      },
    },
  };
};

const checkboxesFieldsErrors = [
  'isCurrentlyServing',
  'isCurrentlyRetired',
  'isNonActivatedMemberOfReserveNationalGuard',
  'isSurvivingSpouse',
  'entitlementCodeFromCoe',
  'branchOfService',
  'militaryStatus',
].reduce((acc, key) => {
  acc[key] = ValidationError.RequiredField;

  return acc;
}, {});

export const customOnFieldChange = ({
  e,
  value,
  entityId,
  setForm,
  setValidationErrors,
  setInForm,
}) => {
  const currentFieldName = e.currentTarget.dataset.fieldname ?? e.currentTarget.name;

  const nameInModel = currentFieldName.split('.').slice(-1)[0];

  const customAction = (sectionForm = {}) => {
    const areFieldsRequired = checkAreFieldsRequired({
      ...sectionForm,
      [nameInModel]: value,
    });

    setValidationErrors(prev => {
      const {
        isCurrentlyServing,
        isCurrentlyRetired,
        isNonActivatedMemberOfReserveNationalGuard,
        isSurvivingSpouse,
        entitlementCodeFromCoe,
        branchOfService,
        militaryStatus,
        ...restErrors
      } = prev[entityId]?.declarations?.militaryService ?? {};

      return {
        ...prev,
        [entityId]: {
          ...prev[entityId],
          declarations: {
            ...prev[entityId]?.declarations,
            militaryService: {
              ...restErrors,
              ...getConditionalObject(areFieldsRequired, checkboxesFieldsErrors),
            },
          },
        },
      };
    });
  };

  customSetInForm({
    declarationType: DeclarationType.MILITARY_SERVICES,
    fieldName: currentFieldName,
    value,
    entityId,
    setForm,
    setInForm,
    customAction,
  });
};
